import { ICorporationResponse } from '@kcftech/prometheus-client';
import { useMutation } from '@tanstack/react-query';
import { TextInlineEditor } from 'components/inline_edit/TextInlineEditor';
import { useNotificationContext } from 'contexts/NotificationContext';
import { usePermissions } from 'contexts/PermissionsContext';
import { infiniteCorporationsDataSourceKey } from 'custom_hooks/data_hooks/data_source/corporationInfiniteDataSource';
import { maxCorporationNameLength } from 'pages/Settings/Corporations/shared';
import { updateCorporation } from 'utils/corporation';
import { queryClient } from 'utils/query';

export type CorporationNameInlineEditorProps = Readonly<{
    corporation: ICorporationResponse;
    onChange: () => void;
    className?: string;
    renderValue?: (value: string | undefined) => React.ReactNode;
}>;

export function CorporationNameInlineEditor({
    corporation,
    onChange,
    renderValue,
    ...rest
}: CorporationNameInlineEditorProps) {
    const { canEditCorporationSettings } = usePermissions();
    const { showToast } = useNotificationContext();
    const { mutate: updateName, isPending } = useMutation({
        meta: { name: 'update-corporation' },
        mutationFn: (name: string) => updateCorporation(corporation, { Name: name }),

        onSuccess: () => {
            onChange();
            queryClient.invalidateQueries({
                queryKey: [`corporation-${corporation.Id}`],
            });
            queryClient.invalidateQueries({
                queryKey: [infiniteCorporationsDataSourceKey],
            });
            showToast({
                type: 'success',
                content: 'Corporation name saved',
            });
        },

        onError: () =>
            showToast({
                type: 'error',
                content: 'Failed to save corporation name',
            }),
    });

    return (
        <TextInlineEditor
            value={corporation.Name}
            isDisabled={isPending || !canEditCorporationSettings}
            renderValue={renderValue}
            onConfirm={(val) => updateName(val ?? '')}
            validate={(val) => {
                if (!val?.trim()) {
                    return 'Corporation name is required';
                }
                if (val.length > maxCorporationNameLength) {
                    return `Corporation name must be ${maxCorporationNameLength} characters or less`;
                }
                return undefined;
            }}
            {...rest}
        />
    );
}
